<script setup lang="ts">
/**
 * Carte de suivi d'un ticket de remplacement de matériel — Onboarding V2 (M7).
 *
 * Affiche l'en-tête (référence, statut, motif, options), le couple
 * ancien → nouveau objet, un stepper des étapes, les actions d'avancement /
 * annulation (déléguées au store) et l'historique repliable. Les objets liés
 * sont retrouvés via le store à partir des ids du ticket.
 */
import { computed, ref, type Component, type FunctionalComponent } from 'vue'
import {
  ArrowLongRightIcon,
  ArrowUturnLeftIcon,
  BellAlertIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  InboxArrowDownIcon,
  ShoppingCartIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import {
  ASSET_STATUS_META,
  REPLACEMENT_FLOW,
  REPLACEMENT_NEXT_ACTION,
  REPLACEMENT_STATUS_META,
  isReplacementActive,
} from '@/v2/onboarding/catalog'
import type { AssetAssignment, ReplacementStatus, ReplacementTicket } from '@/v2/onboarding/types'

const props = defineProps<{
  ticket: ReplacementTicket
}>()

const store = useOnboardingStore()

/* Objets liés (dérivés du store, réactifs aux mutations). */
const caseRef = computed(() => store.getCase(props.ticket.caseId))
const oldAsset = computed(
  () => caseRef.value?.assets.find((a) => a.id === props.ticket.oldAssignmentId) ?? null,
)
const newAsset = computed(
  () => caseRef.value?.assets.find((a) => a.id === props.ticket.newAssignmentId) ?? null,
)

/**
 * Résolution NOM d'icône (fourni par le catalogue) → composant Heroicon outline.
 * Le catalogue ne livre que des chaînes pour rester découplé du package d'icônes.
 */
const ICONS: Record<string, FunctionalComponent> = {
  ClipboardDocumentList: ClipboardDocumentListIcon,
  ShoppingCart: ShoppingCartIcon,
  InboxArrowDown: InboxArrowDownIcon,
  ArrowUturnLeft: ArrowUturnLeftIcon,
  CheckCircle: CheckCircleIcon,
  CheckBadge: CheckBadgeIcon,
  XCircle: XCircleIcon,
}

/** Composant d'icône pour un statut de remplacement (fallback : pastille « clipboard »). */
function statusIcon(status: ReplacementStatus): Component {
  return ICONS[REPLACEMENT_STATUS_META[status].icon] ?? ClipboardDocumentListIcon
}

const statusMeta = computed(() => REPLACEMENT_STATUS_META[props.ticket.status])
const isCancelled = computed(() => props.ticket.status === 'CANCELLED')
const isActive = computed(() => isReplacementActive(props.ticket.status))

/** Index de l'étape courante dans le flux linéaire (−1 si annulé / hors flux). */
const currentIndex = computed(() => REPLACEMENT_FLOW.indexOf(props.ticket.status))

/** Libellé du bouton d'avancement (null si aucune étape suivante). */
const nextActionLabel = computed(() => REPLACEMENT_NEXT_ACTION[props.ticket.status])

/* Confirmation en deux temps pour l'annulation (pas de window.confirm). */
const confirmingCancel = ref(false)

/** N° de série / IMEI d'un objet (chaîne vide si rien). */
function serialOrImei(asset: AssetAssignment | null): string {
  if (!asset) return ''
  return [asset.attributes.serialNumber, asset.attributes.imei].filter((v): v is string => !!v).join(' / ')
}

/** Date ISO/YYYY-MM-DD → DD/MM/YYYY (FR), sans surprise de fuseau pour les jours. */
function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  const day = iso.slice(0, 10)
  const [y, m, d] = day.split('-')
  return y && m && d ? `${d}/${m}/${y}` : iso
}

/** Date ISO complète → date + heure FR (historique). */
function formatDateTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Historique du plus récent au plus ancien. */
const historyDesc = computed(() => [...props.ticket.history].reverse())

async function advance(): Promise<void> {
  await store.advanceReplacement(props.ticket.id)
}

async function cancel(): Promise<void> {
  await store.cancelReplacement(props.ticket.id)
  confirmingCancel.value = false
}
</script>

<template>
  <article class="card border border-base-300 bg-base-100">
    <div class="card-body gap-4">
      <!-- En-tête : référence + statut + motif + options -->
      <header class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0 space-y-1">
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="tabular-nums break-words text-base font-semibold text-base-content">
              {{ ticket.reference }}
            </h3>
            <span class="badge gap-1" :class="statusMeta.badgeClass">
              <component :is="statusIcon(ticket.status)" class="h-4 w-4" aria-hidden="true" />
              {{ statusMeta.label }}
            </span>
          </div>
          <p class="text-base text-base-content/80">Motif : {{ ticket.reason }}</p>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <span v-if="ticket.externalOrder" class="badge badge-outline">Commande externe</span>
          <span
            v-if="ticket.reminderAt"
            class="inline-flex items-center gap-1 text-sm text-warning"
          >
            <BellAlertIcon class="h-4 w-4" aria-hidden="true" />
            <span class="tabular-nums">Échéance {{ formatDay(ticket.reminderAt) }}</span>
          </span>
        </div>
      </header>

      <!-- Bloc Ancien → Nouveau -->
      <section
        class="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr]"
        aria-label="Objet remplacé"
      >
        <!-- Ancien -->
        <div class="min-w-0 rounded-box border border-base-300 bg-base-200/40 p-3">
          <p class="text-sm font-medium text-base-content/80">Ancien matériel</p>
          <p class="mt-1 text-base font-medium break-words text-base-content">{{ oldAsset?.label ?? '—' }}</p>
          <p v-if="serialOrImei(oldAsset)" class="tabular-nums break-all text-sm text-base-content/80">
            {{ serialOrImei(oldAsset) }}
          </p>
          <p class="mt-1 text-sm text-base-content/80">
            {{ oldAsset ? ASSET_STATUS_META[oldAsset.status].label : '—' }}
          </p>
        </div>

        <!-- Flèche -->
        <div class="flex items-center justify-center" aria-hidden="true">
          <ArrowLongRightIcon class="hidden h-6 w-6 text-base-content/80 sm:block" />
          <ArrowLongRightIcon class="h-6 w-6 rotate-90 text-base-content/80 sm:hidden" />
        </div>

        <!-- Nouveau -->
        <div class="min-w-0 rounded-box border border-base-300 bg-base-200/40 p-3">
          <p class="text-sm font-medium text-base-content/80">Nouveau matériel</p>
          <p class="mt-1 text-base font-medium break-words text-base-content">{{ newAsset?.label ?? '—' }}</p>
          <p v-if="serialOrImei(newAsset)" class="tabular-nums break-all text-sm text-base-content/80">
            {{ serialOrImei(newAsset) }}
          </p>
          <p class="mt-1 text-sm text-base-content/80">
            {{ newAsset ? ASSET_STATUS_META[newAsset.status].label : '—' }}
          </p>
        </div>
      </section>

      <!-- État annulé : pas de stepper actif -->
      <div
        v-if="isCancelled"
        class="flex items-center gap-2 rounded-box border border-base-300 bg-base-200/40 p-3 text-base-content/80"
      >
        <XCircleIcon class="h-5 w-5 text-error" aria-hidden="true" />
        <span>Remplacement annulé</span>
      </div>

      <!-- Stepper des étapes : vertical en mobile, horizontal wrappé dès sm -->
      <ol
        v-else
        class="flex flex-col gap-y-2 sm:flex-row sm:flex-wrap sm:gap-x-4"
        aria-label="Étapes du remplacement"
      >
        <li
          v-for="(step, index) in REPLACEMENT_FLOW"
          :key="step"
          class="flex items-center gap-2"
          :aria-current="index === currentIndex ? 'step' : undefined"
        >
          <!-- Étape franchie -->
          <CheckCircleIcon
            v-if="index < currentIndex"
            class="h-5 w-5 text-success"
            aria-hidden="true"
          />
          <!-- Étape courante : point plein -->
          <span
            v-else-if="index === currentIndex"
            class="inline-block h-3 w-3 rounded-full bg-primary"
            aria-hidden="true"
          ></span>
          <!-- Étape à venir : point creux -->
          <span
            v-else
            class="inline-block h-3 w-3 rounded-full border-2 border-base-content/30"
            aria-hidden="true"
          ></span>
          <span
            class="text-sm"
            :class="
              index < currentIndex
                ? 'text-base-content/80'
                : index === currentIndex
                  ? 'font-medium text-base-content'
                  : 'text-base-content/80'
            "
          >
            {{ REPLACEMENT_STATUS_META[step].label }}
          </span>
        </li>
      </ol>

      <!-- Actions -->
      <div v-if="isActive" class="flex flex-wrap items-center gap-2">
        <button
          v-if="nextActionLabel"
          type="button"
          class="btn btn-primary motion-reduce:transition-none"
          @click="advance"
        >
          {{ nextActionLabel }}
        </button>

        <!-- Annulation : confirmation en deux temps -->
        <template v-if="!confirmingCancel">
          <button
            type="button"
            class="btn btn-ghost text-error motion-reduce:transition-none"
            @click="confirmingCancel = true"
          >
            Annuler le remplacement
          </button>
        </template>
        <template v-else>
          <span class="text-sm text-base-content/80">Confirmer l'annulation ?</span>
          <button
            type="button"
            class="btn btn-error motion-reduce:transition-none"
            @click="cancel"
          >
            Oui, annuler
          </button>
          <button
            type="button"
            class="btn btn-ghost motion-reduce:transition-none"
            @click="confirmingCancel = false"
          >
            Non
          </button>
        </template>
      </div>

      <!-- Historique repliable -->
      <details v-if="ticket.history.length" class="collapse collapse-arrow border border-base-300">
        <summary class="collapse-title text-base font-medium">
          Historique ({{ ticket.history.length }})
        </summary>
        <div class="collapse-content">
          <ul role="list" class="space-y-2">
            <li
              v-for="(event, index) in historyDesc"
              :key="index"
              class="flex items-start gap-2 text-sm text-base-content/80"
            >
              <component
                :is="statusIcon(event.toStatus)"
                class="mt-0.5 h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              <span>
                <span class="font-medium text-base-content">
                  {{ REPLACEMENT_STATUS_META[event.toStatus].label }}
                </span>
                — {{ event.by }} —
                <span class="tabular-nums">{{ formatDateTime(event.at) }}</span>
                <span v-if="event.note" class="block text-base-content/80">{{ event.note }}</span>
              </span>
            </li>
          </ul>
        </div>
      </details>
    </div>
  </article>
</template>
